import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, MessageSquare, Bug } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { ContactForm } from "@/components/ContactForm";
import { pageHead, breadcrumbJsonLd, contactPageJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const PATH = "/contact";
const TITLE = "Contact ReadBarcode — Barcode Tool Support & Feedback";
const DESC =
  "Contact ReadBarcode.com for feedback, bug reports, feature suggestions, privacy requests, business inquiries, or corrections related to barcode tools and guides.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Contact", path: PATH },
];

const CONTACT_EMAIL = "hello@readbarcode.com";
const BREADCRUMB_ID = `${absoluteUrl(PATH)}#breadcrumb`;

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () => ({
    ...pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
      ogImage: absoluteUrl("/og/contact.jpg"),
      ogImageAlt:
        "Contact ReadBarcode.com — feedback, bug reports, feature suggestions, privacy and legal inquiries.",
    }),
    scripts: [
      ldScript(
        contactPageJsonLd({
          path: PATH,
          title: TITLE,
          description: DESC,
          breadcrumbId: BREADCRUMB_ID,
        }),
      ),
      ldScript(breadcrumbJsonLd(CRUMBS, BREADCRUMB_ID)),
    ],
  }),
});

function ContactPage() {
  return (
    <ToolLayout
      eyebrow="Contact"
      title="Contact ReadBarcode.com"
      description="Feedback, bug reports, feature suggestions, corrections, privacy requests and business inquiries — one place to reach the operator. Use the form below or email hello@readbarcode.com, and include the page URL and the barcode value when you report a problem with a tool."
      crumbs={CRUMBS}
      content={
        <>
          <h2>What You Can Contact Us About</h2>
          <ul>
            <li>Bug reports</li>
            <li>Feature requests</li>
            <li>Barcode format corrections</li>
            <li>SEO or content corrections on tool and guide pages</li>
            <li>Business inquiries</li>
            <li>Privacy requests (GDPR / CCPA)</li>
            <li>Legal or DMCA notices</li>
            <li>
              Questions about the <Link to="/terms">Terms of Use</Link>
            </li>
          </ul>

          <h2>Privacy and Data Subject Requests</h2>
          <p>
            For privacy-related requests under GDPR, CCPA, or other applicable data protection laws —
            including requests to access, correct, delete, or restrict your personal information — use
            the form above with the category <strong>&ldquo;Privacy request (GDPR / CCPA)&rdquo;</strong>, or email <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Privacy request")}`}
            >
              {CONTACT_EMAIL}
            </a> with the subject line &ldquo;Privacy request&rdquo;. We aim to respond within 30 days as
            required by GDPR. See the <Link to="/privacy">Privacy Policy</Link> for the full list of
            rights available to users in the EEA, the UK, Switzerland and California.
          </p>

          <h2>Legal, DMCA and Trademark Notices</h2>
          <p>
            For legal notices, copyright (DMCA) claims, or trademark concerns, use the form above with
            the category <strong>&ldquo;Legal notice (DMCA / trademark)&rdquo;</strong>, or email <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Legal notice")}`}
            >
              {CONTACT_EMAIL}
            </a> with the subject line &ldquo;Legal notice&rdquo;. Include all information required by
            applicable law for us to evaluate and respond to your request.
          </p>

          <h2>Response Expectations</h2>
          <p>
            We aim to respond to all inquiries within a reasonable timeframe, typically within 30 days
            for privacy requests as required by GDPR. For general feedback and bug reports, response
            times depend on volume and priority. Not all suggestions can be implemented, and we may
            not respond to every message individually.
          </p>

          <h2>A Note on Sensitive Data</h2>
          <p>
            The barcode tools on ReadBarcode.com process camera frames, uploaded images, and the
            values you paste into validators directly in your browser — they are not transmitted to
            us. There is rarely a reason to attach a sensitive barcode payload, Wi-Fi credential,
            vCard, GS1 element string, or regulated identifier to a support email. Where possible,
            describe the issue and include a non-sensitive example or screenshot instead.
          </p>
        </>
      }
    >
      <p className="max-w-3xl text-base leading-7 text-foreground/85">
        Use this page for feedback, bug reports, feature suggestions, correction requests, or
        questions about ReadBarcode.com. You can email us directly using one of the addresses below,
        or send a message through the contact form. Please do not send sensitive barcode data,
        confidential inventory files, private Wi-Fi credentials, medical identifiers, or regulated
        information unless necessary.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <ContactCard
          icon={<Mail className="h-5 w-5" />}
          title="General"
          body={<EmailLink subject="General inquiry" />}
          hint="Best for general questions, feedback and partnership ideas."
        />
        <ContactCard
          icon={<Bug className="h-5 w-5" />}
          title="Bug report"
          body={<EmailLink subject="Bug report" />}
          hint="Include the URL, a screenshot, the barcode value, and your browser/OS."
        />
        <ContactCard
          icon={<MessageSquare className="h-5 w-5" />}
          title="Tool request"
          body={<EmailLink subject="Tool request" />}
          hint="Tell us the format, the use case, and link to a sample symbol if you can."
        />
      </div>

      <section className="mt-12" aria-labelledby="contact-form-heading">
        <h2 id="contact-form-heading" className="text-xl font-semibold tracking-tight md:text-2xl">
          Send Us a Message
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Prefer not to open your email client? Send us a message directly — we&apos;ll reply to the
          email address you provide.
        </p>
        <div className="mt-4 max-w-3xl">
          <ContactForm />
        </div>
      </section>
    </ToolLayout>
  );
}

function EmailLink({ subject }: { subject: string }) {
  const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  return (
    <a href={href} className="font-medium text-primary underline underline-offset-4">
      {CONTACT_EMAIL}
    </a>
  );
}

function ContactCard({
  icon,
  title,
  body,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  hint: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-5">
      <div className="flex items-center gap-2 text-primary">
        {icon}
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </div>
      <div className="mt-2 text-sm text-foreground">{body}</div>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
